import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { Finding, RoadDetail, Section } from "../../api/types";
import { ConditionPill, PriorityChip } from "../../components/Badges";
import { ChainageStrip } from "../../components/ChainageStrip";
import { CountBars, KpiTile } from "../../components/Charts";
import { DataTable } from "../../components/DataTable";
import { Panel } from "../../components/Layout";
import { EmptyState } from "../../components/States";
import { chainage, num } from "../../lib/format";

const kes = (v: number) => (v >= 1e6 ? `KES ${(v / 1e6).toFixed(1)}M` : `KES ${Math.round(v).toLocaleString("en-KE")}`);

export function SummaryTab({ road }: { road: RoadDetail }) {
  const s = road.survey;
  const f = useQuery({ queryKey: ["road", s.survey_uuid, "findings", "all", false], queryFn: () => api<{ items: Finding[] }>(`/api/roads/${s.survey_uuid}/findings`) });
  const d = road.summary.by_domain;
  const base = `/roads/${s.survey_uuid}/findings`;
  const cost = road.sections.reduce((t, x) => t + (x.indicative_cost_kes ?? 0), 0);

  return (
    <>
      <div className="kpis">
        <KpiTile label="Road condition findings" value={num(d.condition.verified)} note={d.condition.unreviewed ? `+${d.condition.unreviewed} unreviewed` : "verified"} to={`${base}?domain=condition`} />
        <KpiTile label="Assets & inventory" value={num(d.assets.verified)} note={d.assets.unreviewed ? `+${d.assets.unreviewed} unreviewed` : "verified"} to={`${base}?domain=assets`} />
        <KpiTile label="Road safety" value={num(d.safety.verified)} note={d.safety.unreviewed ? `+${d.safety.unreviewed} unreviewed` : "verified"} to={`${base}?domain=safety`} />
        <KpiTile label="Images" value={num(s.images)} to={`/roads/${s.survey_uuid}/images`} />
        {cost > 0 ? <KpiTile label="Indicative works cost" value={kes(cost)} note="From section treatments and profile rates" /> : null}
      </div>
      {s.pending_review ? (
        <p className="notice">The condition is provisional: {s.pending_review} AI finding{s.pending_review === 1 ? " is" : "s are"} still waiting for an engineer. Only verified defects count towards the rating.</p>
      ) : null}

      <Panel title="Condition along the road" id="sum-strip">
        <div className="stack">
          <ChainageStrip lengthKm={s.length_km} sections={road.sections} findings={f.data?.items ?? []} />
          <p className="muted" style={{ fontSize: 13 }}>
            {s.start_name || "Start"} → {s.end_name || "End"}. Ticks mark findings: solid for verified, faded for unreviewed.
            {!road.sections.length ? " No condition assessment has been generated for this survey yet." : ""}
          </p>
        </div>
      </Panel>

      <div className="grid grid-2">
        <Panel title="Condition sections" id="sum-sections" flush>
          <DataTable<Section>
            rows={road.sections}
            rowKey={(r) => `${r.start_m}-${r.end_m}`}
            caption="Condition sections"
            maxHeight={420}
            empty={<EmptyState title="No condition sections yet">Sections, priorities and treatments appear once the decision engine has assessed this survey.</EmptyState>}
            columns={[
              { key: "sec", label: "Section", sort: (r) => r.start_m, render: (r) => <span className="mono">{chainage(r.start_m)}–{chainage(r.end_m).replace("km ", "")}</span> },
              { key: "band", label: "Condition", sort: (r) => r.condition_score, render: (r) => <><ConditionPill value={r.condition_band} provisional={r.pending_review > 0} /> <span className="muted num">{r.condition_score?.toFixed(0)}</span></> },
              { key: "prio", label: "Priority", sort: (r) => r.priority, render: (r) => <PriorityChip value={r.priority_band} /> },
              { key: "treat", label: "Treatment", sort: (r) => r.treatment, render: (r) => <span title={r.treatment_rationale ?? undefined}>{r.treatment || "—"}</span> },
              { key: "cost", label: "Cost (KES)", align: "right", sort: (r) => r.indicative_cost_kes, render: (r) => (r.indicative_cost_kes ? Math.round(r.indicative_cost_kes).toLocaleString("en-KE") : "—") },
              { key: "f3", label: "In 3 years", align: "right", sort: (r) => r.forecast_3y, render: (r) => r.forecast_3y?.toFixed(0) ?? "—" },
              { key: "ev", label: "Evidence", render: (r) => <span className="muted" style={{ fontSize: 12 }}>{r.verified_defects} verified{r.pending_review ? `, ${r.pending_review} to review` : ""}</span> },
            ]}
          />
        </Panel>
        <Panel title="Findings by type" id="sum-types">
          <CountBars rows={road.summary.by_type} empty="No findings on this road yet" />
          {road.summary.by_type.length ? <p style={{ marginTop: 12 }}><Link to={base}>See all findings on the map</Link></p> : null}
        </Panel>
      </div>
    </>
  );
}
